import React, { useState, useEffect } from 'react';
import { Button, Form, Spinner, Alert } from 'react-bootstrap';
import {
  useEticketingBusesQuery,
  useEticketingCitiesQuery,
  useEticketingCreateRouteMutation,
  useEticketingServicesQuery,
  useEticketingUpdateRouteMutation,
} from '../../../../api/e-ticketing/e-ticketing.hooks';
import { useUploadFile } from '../../../../api/fileUpload/fileUpload.hooks';
import { getCurrentUser } from '../../PluginsMenu/Nestable/utils';

const RouteForm = ({ route, onSuccess, onError, onCancel, serviceId, service }) => {
  const isEditMode = !!route;

  const isSightSeeing = service === 'sightseeing';
  const { city_id } = getCurrentUser();
  
  // Determine if user is admin (no city_id) or sub admin (has city_id)
  const isSubAdmin = !!city_id;
  const isAdmin = !city_id;

  const { update: updateRoute, isLoading: updating } = useEticketingUpdateRouteMutation();
  const { create: createRoute, isLoading: creating } = useEticketingCreateRouteMutation();
  const { data: busesData, isLoading: loadingBuses, error: loadingBusesError } = useEticketingBusesQuery();
  const { data: citiesData, isLoading: loadingCities, error: loadingCitiesError } = useEticketingCitiesQuery();
  const { upload, isLoading: isUploading } = useUploadFile();
  const { data: servicesData, isLoading: loadingServices, error: loadingServicesError } = useEticketingServicesQuery();

  const buses = busesData || [];
  const cities = citiesData || [];
  const services = servicesData || [];
  const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    image: '',
    cityId: route?.cityId || city_id || '',
    service_id: serviceId || '',
    address: '',
    phone: '',
    terminal: '',
    days: [],
    is_schedule: false,
    date_from: '',
    date_to: '',
    busId: '',
  });

  useEffect(() => {
    if (route) {
      setFormData({
        name: route.name || '',
        description: route.description || '',
        image: route.image || '',
        cityId: route.cityId || '',
        service_id: route.service_id || '',
        address: route.address || '',
        phone: route.phone || '',
        terminal: route.terminal || '',
        days: route.days ? route.days.split(',') : [],
        is_schedule: route.is_schedule || false,
        date_from: route.date_from || '',
        date_to: route.date_to || '',
        busId: (isSightSeeing && route.bus?.id) ? route.bus.id : '',
      });
    }
  }, [route, isSightSeeing]);

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleDayToggle = (day) => {
    setFormData((prev) => ({
      ...prev,
      days: prev.days.includes(day)
        ? prev.days.filter(d => d !== day)
        : [...prev.days, day]
    }));
  };

  const handleSelectAllDays = (e) => {
    const isChecked = e.target.checked;
    setFormData((prev) => ({
      ...prev,
      days: isChecked ? [...daysOfWeek] : []
    }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const result = await upload(file);
      setFormData((prev) => ({
        ...prev,
        image: result.url,
      }));
    } catch (err) {
      onError('Failed to upload image');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const submitData = {
      ...formData,
      days: formData.days.join(','),
    };

    // Remove busId if not sightseeing service
    if (!isSightSeeing) {
      delete submitData.busId;
    }

    try {
      if (isEditMode) {
        await updateRoute(route.id, submitData);
        onSuccess('Route updated successfully.');
      } else {
        await createRoute(submitData);
        onSuccess('Route created successfully.');
      }
    } catch (err) {
      onError(err?.message || `Failed to ${isEditMode ? 'update' : 'create'} route.`);
    }
  };

  return (
    <div className="card">
      <div className="card-header d-flex justify-content-between align-items-center">
        <h5 className="card-title m-0">{isEditMode ? 'Edit Route' : 'Add Route'}</h5>
        <button className="btn btn-sm btn-secondary" onClick={onCancel}>
          <i className="fa fa-times me-1"></i> Cancel
        </button>
      </div>
      <div className="card-body">
        {isSightSeeing && loadingBusesError && (
          <Alert variant="warning" className="mb-3">
            Failed to load buses. You can still create a route without selecting a bus.
          </Alert>
        )}
        {loadingCitiesError && (
          <Alert variant="warning" className="mb-3">
            Failed to load cities. You can still create a route without selecting a city.
          </Alert>
        )}
        {loadingServicesError && (
          <Alert variant="warning" className="mb-3">
            Failed to load services. You can still create a route without selecting a service.
          </Alert>
        )}
        
        <Form onSubmit={handleSubmit}>
          <div className="row">
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>Route Name *</Form.Label>
                <Form.Control
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleFormChange}
                  placeholder="Enter route name"
                  required
                />
              </Form.Group>
            </div>

            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>Description *</Form.Label>
                <Form.Control
                  type="text"
                  name="description"
                  value={formData.description}
                  onChange={handleFormChange}
                  placeholder="Enter route description"
                  required
                />
              </Form.Group>
            </div>

            {isSightSeeing && (
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Select Bus</Form.Label>
                  <Form.Select
                    name="busId"
                    value={formData.busId}
                    onChange={handleFormChange}
                    disabled={loadingBuses}
                  >
                    <option value="">-- Select a bus --</option>
                    {buses.map((bus) => (
                      <option key={bus.id} value={bus.id}>
                        {bus.name}
                      </option>
                    ))}
                  </Form.Select>
                  {loadingBuses && (
                    <Form.Text className="text-muted">
                      <Spinner animation="border" size="sm" className="me-1" />
                      Loading buses...
                    </Form.Text>
                  )}
                </Form.Group>
              </div>
            )}

            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>City *</Form.Label>
                <Form.Select
                  name="cityId"
                  value={formData.cityId}
                  onChange={handleFormChange}
                  disabled={loadingCities || isSubAdmin}
                  required
                >
                  <option value="">-- Select a city --</option>
                  {cities.map((city) => (
                    <option key={city.id} value={city.id}>
                      {city.name}
                    </option>
                  ))}
                </Form.Select>
                {loadingCities && (
                  <Form.Text className="text-muted">
                    <Spinner animation="border" size="sm" className="me-1" />
                    Loading cities...
                  </Form.Text>
                )}
                {isSubAdmin && (
                  <Form.Text className="text-muted">
                    City is assigned to your account and cannot be changed
                  </Form.Text>
                )}
              </Form.Group>
            </div>

            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>Service *</Form.Label>
                <Form.Select
                  name="service_id"
                  value={formData.service_id}
                  // disabled={loadingServices}
                  disabled
                  required
                >
                  <option value="">-- Select a service --</option>
                  {services.map((service) => (
                    <option key={service.id} value={service.id}>
                      {service.service_name}
                    </option>
                  ))}
                </Form.Select>
                {loadingServices && (
                  <Form.Text className="text-muted">
                    <Spinner animation="border" size="sm" className="me-1" />
                    Loading services...
                  </Form.Text>
                )}
              </Form.Group>
            </div>

            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>Image</Form.Label>
                <div>
                  {formData.image ? (
                    <div className="position-relative d-inline-block">
                      <img
                        src={formData.image}
                        alt="Route"
                        style={{ width: '150px', height: '100px', objectFit: 'cover', borderRadius: '4px' }}
                      />
                      <Button
                        variant="danger"
                        size="sm"
                        className="position-absolute top-0 end-0"
                        style={{ transform: 'translate(50%, -50%)' }}
                        onClick={() => setFormData(prev => ({ ...prev, image: '' }))}
                      >
                        <i className="fa fa-times"></i>
                      </Button>
                    </div>
                  ) : (
                    <Button
                      variant="outline-primary"
                      as="label"
                      disabled={isUploading}
                    >
                      {isUploading ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-1" />
                          Uploading...
                        </>
                      ) : (
                        <>
                          <i className="fa fa-upload me-1"></i> Upload Image
                        </>
                      )}
                      <input
                        type="file"
                        hidden
                        accept="image/*"
                        onChange={handleImageUpload}
                      />
                    </Button>
                  )}
                </div>
              </Form.Group>
            </div>

            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>Address</Form.Label>
                <Form.Control
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleFormChange}
                  placeholder="Enter address"
                />
              </Form.Group>
            </div>

            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>Phone</Form.Label>
                <Form.Control
                  type="number"
                  name="phone"
                  value={formData.phone}
                  onChange={handleFormChange}
                  placeholder="Enter phone number"
                />
              </Form.Group>
            </div>

            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>Terminal</Form.Label>
                <Form.Control
                  type="text"
                  name="terminal"
                  value={formData.terminal}
                  onChange={handleFormChange}
                  placeholder="Enter terminal"
                />
              </Form.Group>
            </div>

            <div className="col-12">
              <Form.Group className="mb-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <Form.Label className="mb-0">Operating Days</Form.Label>
                  <Form.Check
                    type="checkbox"
                    id="select-all-days"
                    label="Select All"
                    checked={formData.days.length === daysOfWeek.length}
                    onChange={handleSelectAllDays}
                  />
                </div>
                <div className="d-flex flex-wrap gap-2">
                  {daysOfWeek.map(day => (
                    <Form.Check
                      key={day}
                      type="checkbox"
                      id={`day-${day}`}
                      label={day}
                      checked={formData.days.includes(day)}
                      onChange={() => handleDayToggle(day)}
                    />
                  ))}
                </div>
              </Form.Group>
            </div>

            <div className="col-md-12">
              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  name="is_schedule"
                  label="Is Scheduled?"
                  checked={formData.is_schedule}
                  onChange={handleFormChange}
                />
              </Form.Group>
            </div>

            {formData.is_schedule && (
              <>
                <div className="col-md-6">
                  <Form.Group className="mb-3">
                    <Form.Label>Date From</Form.Label>
                    <Form.Control
                      type="date"
                      name="date_from"
                      value={formData.date_from}
                      onChange={handleFormChange}
                    />
                  </Form.Group>
                </div>

                <div className="col-md-6">
                  <Form.Group className="mb-3">
                    <Form.Label>Date To</Form.Label>
                    <Form.Control
                      type="date"
                      name="date_to"
                      value={formData.date_to}
                      onChange={handleFormChange}
                    />
                  </Form.Group>
                </div>
              </>
            )}
          </div>

          <div className="d-flex justify-content-end gap-2">
            <Button variant="secondary" onClick={onCancel}>
              Cancel
            </Button>
            <Button
              variant="primary"
              type="submit"
              disabled={updating || creating}
            >
              {updating || creating ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  {isEditMode ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                isEditMode ? 'Update Route' : 'Add Route'
              )}
            </Button>
          </div>
        </Form>
      </div>
    </div>
  );
};

export default RouteForm;
